import React, { ReactElement } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage, FormattedNumber, IntlShape, useIntl } from 'react-intl';

import {
    BaseStyleEnums,
    ReactTableEnums,
    ShortCodeEnums,
    CoreCommonMessages,
    CoreUtils,
    TimeZoneEnums,
} from '@nhl/nhl-breakaway-ui';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';
import { ColumnDef, ColumnHelper, createColumnHelper } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { GAME_TIME } from '~/core/dateTimeFormats';
import { CountryCode, PageName } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { gameLinksColumn, networkColumn } from '~/core/tableColumns/scheduleTableColumns/columns';
import { getGameLinks, getPartnerLinks, useLinksWithRecap } from '~/core/tableColumns/scheduleTableColumns/utils';
import { BettingOddPartner } from '~/core/types';
import { isValidLinkScheduleState, parseValueFromLocalizedAttribute } from '~/core/utils';
import { useGetWebStreamURLQuery } from '~/redux/ducks/webStreamURL';
import { GameLinksPopperWrapper } from '~/views/components/gameLinksPopper/GameLinksPopper';
import { getEventDetails } from '~/views/pages/schedule/components/listView/components/dayOverview/utils';
import { NeutralSiteIndicator } from '~/views/pages/schedule/components/neutralSiteIndicator/NeutralSiteIndicator';
import SpecialEventMatchup from '~/views/pages/schedule/components/specialEventMatchup/SpecialEventMatchup';

import { ScheduleGame, Team as ScheduleTeam, TeamOdds } from '../../../../types';

import {
    eventColumnStyle,
    gameScoreColumnStyle,
    gameScoreHeaderStyle,
    gameScoreStyle,
    gameWinnersColumnStyle,
    matchupColumnStyle,
    matchupHeaderStyle,
    timeColumnStyle,
    timeHeaderStyle,
} from './columnStyles';

import {
    AtIndicator,
    EventContainer,
    EventIndicator,
    GameInfoColumn,
    GameWinner,
    LiveGameScore,
    MatchUpColumn,
    NeutralSite,
    OddDisplay,
    SIZES,
    SpecialEventDescription,
    SpecialEventImage,
    SpecialEventInfo,
    Team,
    TeamContainer,
    TeamName,
    TeamNameContainer,
    WinningTeamGameScore,
} from './styles';

const getTime = (time: string, timezone: TimeZoneEnums | string): string =>
    DateTime.fromISO(time).setZone(timezone).toLocaleString(GAME_TIME);

const getOdds = (oddsProviderId: number | undefined, odds: Array<TeamOdds> | undefined): number | undefined => {
    const odd = odds?.find((odd) => odd.providerId === oddsProviderId)?.value;
    return odd === undefined ? odd : parseFloat(odd);
};

const columnHelper: ColumnHelper<ScheduleGame> = createColumnHelper<ScheduleGame>();

const EVENT_COLUMN: ColumnDef<ScheduleGame> = columnHelper.display({
    id: 'event',
    size: 12,
    maxSize: 12,
    header: '',
    cell: function CellComponent(data) {
        const game = data.row.original;
        const eventDetails = getEventDetails(game);
        const screenSize = useSizeWithThrottle();

        return (
            <GameInfoColumn>
                <EventContainer $eventType={eventDetails.eventType}>
                    <EventIndicator $eventType={eventDetails.eventType} />
                </EventContainer>
                {eventDetails.content && <SpecialEventDescription>{eventDetails.content}</SpecialEventDescription>}
                {game.neutralSite && screenSize.width < BaseStyleEnums.ScreenWidthBreakpoints.large && (
                    <NeutralSiteIndicator />
                )}
            </GameInfoColumn>
        );
    },
    meta: {
        className: 'scheduleColumn',
        headerClassName: 'scheduleHeader',
        style: eventColumnStyle,
    },
});

const matchupColumn = (
    bettingOddPartner: BettingOddPartner | undefined,
    intl: IntlShape,
    countryCode: CountryCode,
    showMoneyline?: boolean
): ColumnDef<ScheduleGame> =>
    columnHelper.display({
        id: 'matchup',
        size: SIZES.matchup.size,
        maxSize: SIZES.matchup.maxSize,
        header: (context) => (
            <>
                <div>
                    <FormattedMessage {...CommonMessages.tableHeaders.long.matchup} />
                </div>
                {context.table.options.meta?.showBettingOdds && showMoneyline && (
                    <OddDisplay $aligned="center">
                        <FormattedMessage
                            description="The type of betting odds we are displaying. This is a subtitle wrapped in ()"
                            defaultMessage="(Moneyline)"
                        />
                    </OddDisplay>
                )}
            </>
        ),
        cell: (context) => {
            const game = context.row.original;
            const meta = context.table.options.meta;
            const eventDetails = getEventDetails(game);
            const showOddsSign = [CountryCode.Canada, CountryCode.UnitedStates].includes(
                countryCode.toUpperCase() as CountryCode
            );
            const isNonSpecialEventWithDetails = eventDetails.content && !game.specialEvent;

            const getTeam = (team: ScheduleTeam, isHome: boolean): ReactElement => {
                const odds = meta?.showBettingOdds ? getOdds(bettingOddPartner?.partnerId, team?.odds) : undefined;

                return (
                    <Team $isHome={isHome}>
                        <TeamNameContainer $isHome={isHome} $hasNoLogo={!team.logo}>
                            <TeamName>
                                {meta?.isSmall ? team.abbrev : parseValueFromLocalizedAttribute(team.commonName)}
                            </TeamName>
                            {meta?.showBettingOdds && odds && (
                                <OddDisplay $aligned={isHome ? 'left' : 'right'}>
                                    <FormattedNumber value={odds} signDisplay={showOddsSign ? 'always' : 'negative'} />
                                </OddDisplay>
                            )}
                        </TeamNameContainer>
                        {team.logo && !game.specialEvent && (
                            <TeamDisplay
                                logoUrlOverride={team.logo}
                                team={{ triCode: team.abbrev }}
                                displayName={false}
                                altTextOverride=""
                            />
                        )}
                    </Team>
                );
            };

            const matchupDescription = intl.formatMessage(CommonMessages.gameLocationDescriptions.long.matchup, {
                awayTeam: parseValueFromLocalizedAttribute(game.awayTeam.placeName),
                homeTeam: parseValueFromLocalizedAttribute(game.homeTeam.placeName),
            });

            const getTopMargin = (): number => {
                const baseMargin = game.neutralSite ? 10 : 0;
                return isNonSpecialEventWithDetails ? 16 + baseMargin : baseMargin;
            };

            return (
                <>
                    <MatchUpColumn
                        $isSpecialEvent={!!game.specialEvent}
                        $topMargin={getTopMargin()}
                        aria-label={matchupDescription}
                    >
                        <TeamContainer>
                            {getTeam(game.awayTeam, false)}
                            {game.specialEvent ? (
                                game.specialEvent?.lightLogoUrl ? (
                                    <SpecialEventImage
                                        src={parseValueFromLocalizedAttribute(
                                            game.specialEvent.lightLogoUrl,
                                            intl.locale
                                        )}
                                    />
                                ) : (
                                    <SpecialEventMatchup
                                        game={game}
                                        intl={intl}
                                        specialEvent={game.specialEvent}
                                        isCalendarView={false}
                                        allowTruncatedText={true}
                                    />
                                )
                            ) : (
                                <AtIndicator>@</AtIndicator>
                            )}
                            {getTeam(game.homeTeam, true)}
                        </TeamContainer>
                    </MatchUpColumn>
                    {isNonSpecialEventWithDetails && <SpecialEventInfo>{eventDetails.content}</SpecialEventInfo>}
                    {game.neutralSite && (
                        <NeutralSite>
                            <NeutralSiteIndicator />
                        </NeutralSite>
                    )}
                </>
            );
        },
        meta: {
            className: 'scheduleColumn',
            style: matchupColumnStyle,
            headerStyle: matchupHeaderStyle,
            scope: ReactTableEnums.Scopes.ROW,
        },
    });

const TIME_COLUMN = (
    isPreGame: boolean,
    pageName: PageName,
    showOnlyHomeTickets: boolean,
    teamTriCode?: string
): ColumnDef<ScheduleGame> =>
    columnHelper.display({
        id: 'time',
        size: SIZES.time.size,
        maxSize: SIZES.time.maxSize,
        header: () => <FormattedMessage {...CommonMessages.tableHeaders.long.gameTime} />,
        cell: function CellComponent(data) {
            const isWebview = useIsWebview();

            const game = data.row.original;
            const meta = data.table.options.meta;

            const ticketsLink = useOptionForLocale({
                [ShortCodeEnums.Language.English]: game.ticketsLink,
                [ShortCodeEnums.Language.French]: game.ticketsLinkFr,
            });

            if (isValidLinkScheduleState(game.gameScheduleState)) {
                const timezone =
                    meta?.timezone === TimeZoneEnums.VENUE
                        ? game.venueTimezone
                        : meta?.timezone || TimeZoneEnums.AMERICA_NEW_YORK;

                const gameLinks =
                    pageName === PageName.ClubSchedule
                        ? [
                              ...getGameLinks({
                                  game,
                                  pageName,
                                  showOnlyHomeTickets,
                                  ticketsLink,
                                  teamTriCode,
                                  isWebview,
                              }),
                              ...getPartnerLinks(game, data.row.original.homeTeam.abbrev.toLowerCase() === teamTriCode),
                          ]
                        : getGameLinks({ game, pageName, showOnlyHomeTickets, ticketsLink, teamTriCode, isWebview });

                return (
                    <GameLinksPopperWrapper game={game} gameLinks={gameLinks}>
                        {game.gameScheduleState === ShortCodeEnums.GameScheduleState.scheduled ? (
                            isPreGame && getTime(game.startTimeUTC, timezone)
                        ) : (
                            <CoreUtils.SafeFormattedMessage
                                message={
                                    CoreCommonMessages.gameScheduleStateDescriptions.abbreviations[
                                        game.gameScheduleState
                                    ]
                                }
                            />
                        )}
                    </GameLinksPopperWrapper>
                );
            }

            return (
                <CoreUtils.SafeFormattedMessage
                    message={CoreCommonMessages.gameScheduleStateDescriptions.abbreviations[game.gameScheduleState]}
                />
            );
        },
        meta: {
            className: 'scheduleColumn fullWidth',
            headerClassName: 'scheduleHeader',
            style: timeColumnStyle(isPreGame),
            headerStyle: timeHeaderStyle(isPreGame),
        },
    });

const GAME_RESULT_COLUMN = (pageName: PageName): ColumnDef<ScheduleGame> =>
    columnHelper.display({
        id: 'gameResult',
        size: SIZES.gameScore.size,
        maxSize: SIZES.gameScore.maxSize,
        header: () => (
            <FormattedMessage
                description="A table header that displays the result of the game. Ex. CAR 1, NYI 0 (OT)"
                defaultMessage="Result"
            />
        ),
        cell: function CellComponent(data) {
            const intl = useIntl();
            const isWebview = useIsWebview();

            const game = data.row.original;
            const winningTeam = game.homeTeam.score > game.awayTeam.score ? game.homeTeam : game.awayTeam;
            const losingTeam = game.homeTeam.score > game.awayTeam.score ? game.awayTeam : game.homeTeam;

            const ticketsLink = useOptionForLocale({
                [ShortCodeEnums.Language.English]: game.ticketsLink,
                [ShortCodeEnums.Language.French]: game.ticketsLinkFr,
            });

            const gameLinks = useLinksWithRecap({
                game,
                showOnlyHomeTickets: false,
                ticketsLink,
                isWebview,
                pageName,
            });

            return (
                <GameLinksPopperWrapper game={game} gameLinks={gameLinks}>
                    <FormattedMessage
                        description="A table column that displays the result of the game. Ex. CAR 1, NYI 0 (OT)"
                        defaultMessage="<b>{winningTeam} {winningTeamScore}</b>, {losingTeam} {losingTeamScore} <periodInfo>({period})</periodInfo>"
                        values={{
                            b: (text) => <WinningTeamGameScore>{text}</WinningTeamGameScore>,
                            winningTeam: winningTeam.abbrev,
                            winningTeamScore: winningTeam.score,
                            losingTeam: losingTeam.abbrev,
                            losingTeamScore: losingTeam.score,
                            periodInfo: (item) =>
                                game.periodDescriptor.periodType === ShortCodeEnums.PeriodType.regulation ? null : item,
                            period: CoreUtils.getPeriodDisplayFromDescriptor(
                                intl,
                                game.periodDescriptor,
                                game.gameType
                            ),
                        }}
                    />
                </GameLinksPopperWrapper>
            );
        },
        meta: {
            className: 'scheduleColumn fullWidth',
            headerClassName: 'scheduleHeader gameResultColumnHeader',
            style: gameScoreStyle,
        },
    });

const GAME_SCORE_COLUMN = (countryCode: CountryCode, pageName: PageName): ColumnDef<ScheduleGame> =>
    columnHelper.display({
        id: 'gameScore',
        size: SIZES.gameScore.size,
        maxSize: SIZES.gameScore.maxSize,
        header: () => <FormattedMessage {...CommonMessages.statsDescriptions.long.score} />,
        cell: function CellComponent(data) {
            const isWebview = useIsWebview();
            const intl = useIntl();

            const game = data.row.original;
            const winningTeam = game.homeTeam.score > game.awayTeam.score ? game.homeTeam : game.awayTeam;
            const losingTeam = game.homeTeam.score > game.awayTeam.score ? game.awayTeam : game.homeTeam;

            const ticketsLink = useOptionForLocale({
                [ShortCodeEnums.Language.English]: game.ticketsLink,
                [ShortCodeEnums.Language.French]: game.ticketsLinkFr,
            });

            const { data: streamUrl } = useGetWebStreamURLQuery(
                {
                    broadcasts: game.tvBroadcasts,
                    countryCode,
                    language: intl.locale as ShortCodeEnums.Language,
                },
                { skip: countryCode === CountryCode.None }
            );

            return (
                <GameLinksPopperWrapper
                    game={game}
                    gameLinks={getGameLinks({
                        game,
                        pageName,
                        showOnlyHomeTickets: false,
                        ticketsLink,
                        streamUrl,
                        isWebview,
                    })}
                >
                    <LiveGameScore>
                        <FormattedMessage
                            description="A table column that displays the result of the game. Ex. CAR 1, NYI 0 (OT)"
                            defaultMessage="<b>{winningTeam} {winningTeamScore}</b>, {losingTeam} {losingTeamScore} <periodInfo>({period})</periodInfo>"
                            values={{
                                b: (text) => text,
                                winningTeam: winningTeam.abbrev,
                                winningTeamScore: winningTeam.score,
                                losingTeam: losingTeam.abbrev,
                                losingTeamScore: losingTeam.score,
                                periodInfo: (item) =>
                                    game.periodDescriptor.periodType === ShortCodeEnums.PeriodType.regulation
                                        ? null
                                        : item,
                                period: CoreUtils.getPeriodDisplayFromDescriptor(
                                    intl,
                                    game.periodDescriptor,
                                    game.gameType
                                ),
                            }}
                        />
                    </LiveGameScore>
                </GameLinksPopperWrapper>
            );
        },
        meta: {
            className: 'scheduleColumn fullWidth',
            headerClassName: 'scheduleHeader gameScoreColumnHeader',
            style: gameScoreColumnStyle,
            headerStyle: gameScoreHeaderStyle,
        },
    });

const GAME_WINNER_COLUMN = (isLarge: boolean): ColumnDef<ScheduleGame> =>
    columnHelper.display({
        id: 'gameWinners',
        size: SIZES.gameWinner.size,
        header: () => (
            <FormattedMessage
                description="A table header that displays the Game Winners"
                defaultMessage="Winning Goalie & Game-Winning Goal"
            />
        ),
        cell: (data) => {
            const game = data.row.original;
            const hasWinningGoalie = game?.winningGoalie?.firstInitial;
            const hasWinningGoalScorer = game?.winningGoalScorer?.firstInitial;
            const winningGoalie = `${parseValueFromLocalizedAttribute(
                game?.winningGoalie?.firstInitial
            )} ${parseValueFromLocalizedAttribute(game?.winningGoalie?.lastName)}`;
            const winningGoal = `${parseValueFromLocalizedAttribute(
                game?.winningGoalScorer?.firstInitial
            )} ${parseValueFromLocalizedAttribute(game?.winningGoalScorer?.lastName)}`;

            if (hasWinningGoalie && hasWinningGoalScorer && isLarge) {
                return (
                    <GameWinner>
                        <FormattedMessage
                            description="A table column that displays the Game Winners Ex. I. Shesterkin & M. Zibanejad"
                            defaultMessage="{winningGoalie} & {winningGoal}"
                            values={{ winningGoalie, winningGoal }}
                        />
                    </GameWinner>
                );
            } else if (hasWinningGoalie || hasWinningGoalScorer) {
                return (
                    <GameWinner $withPrefix={hasWinningGoalie !== undefined && hasWinningGoalScorer !== undefined}>
                        {hasWinningGoalie && (
                            <div>
                                <FormattedMessage
                                    description="A row that displays the name of the winning goalie"
                                    defaultMessage="<b>Winning Goalie:</b> {winningGoalie}"
                                    values={{
                                        b: (text) => <b>{text}</b>,
                                        winningGoalie,
                                    }}
                                />
                            </div>
                        )}
                        {hasWinningGoalScorer && (
                            <div>
                                <FormattedMessage
                                    description="A row that displays the name of the scorer of the game-winning goal"
                                    defaultMessage="<b>Game-Winning Goal:</b> {winningGoal}"
                                    values={{
                                        b: (text) => <b>{text}</b>,
                                        winningGoal,
                                    }}
                                />
                            </div>
                        )}
                    </GameWinner>
                );
            }

            return (
                <GameWinner>
                    <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
                </GameWinner>
            );
        },
        meta: {
            className: 'scheduleColumn',
            headerClassName: 'scheduleHeader',
            style: gameWinnersColumnStyle,
        },
    });

export const preGameColumns = (
    bettingOddPartner: BettingOddPartner | undefined,
    intl: IntlShape,
    location: CountryCode,
    pageName: PageName,
    showOnlyHomeTickets: boolean,
    teamTriCode?: string
): ColumnDef<ScheduleGame>[] => [
    EVENT_COLUMN,
    matchupColumn(bettingOddPartner, intl, location, true),
    TIME_COLUMN(true, pageName, showOnlyHomeTickets, teamTriCode),
    networkColumn(location, teamTriCode !== undefined, PageName.Schedule) as ColumnDef<ScheduleGame>,
    gameLinksColumn({
        intl,
        showOnlyHomeTickets,
        teamTriCode,
        countryCode: location,
        pageName,
    }) as ColumnDef<ScheduleGame>,
];

export const liveGameColumns = (
    bettingOddPartner: BettingOddPartner | undefined,
    intl: IntlShape,
    location: CountryCode,
    pageName: PageName,
    teamTriCode?: string
): ColumnDef<ScheduleGame>[] => [
    EVENT_COLUMN,
    matchupColumn(bettingOddPartner, intl, location),
    GAME_SCORE_COLUMN(location, pageName),
    networkColumn(location, teamTriCode !== undefined, PageName.Schedule) as ColumnDef<ScheduleGame>,
    gameLinksColumn({ intl, showOnlyHomeTickets: true, countryCode: location, pageName }) as ColumnDef<ScheduleGame>,
];

export const postGameColumns = (
    bettingOddPartner: BettingOddPartner | undefined,
    intl: IntlShape,
    location: CountryCode,
    pageName: PageName,
    isLarge: boolean
): ColumnDef<ScheduleGame>[] => [
    EVENT_COLUMN,
    matchupColumn(bettingOddPartner, intl, location),
    GAME_RESULT_COLUMN(pageName),
    GAME_WINNER_COLUMN(isLarge),
    gameLinksColumn({ intl, showOnlyHomeTickets: false, countryCode: location, pageName }) as ColumnDef<ScheduleGame>,
];
